import { useMemo } from "react";
import type { AllDayTripNote, DayView, TripLeg } from "@poi-planner/shared";
import { useLegStore } from "../../store/legStore";
import { packAllDayLanes } from "../../utils/legLanes";
import { CalendarLegBand } from "./CalendarLegBand";
import { WeekAllDayRail } from "./WeekAllDayRail";

/** The legs that touch any of `dayViews`' dates, in store order. */
function useVisibleLegs(dayViews: DayView[]): TripLeg[] {
  const legs = useLegStore((state) => state.legs);
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews[dayViews.length - 1]?.date;
  return useMemo(
    () =>
      firstDate && lastDate
        ? legs.filter(
            (leg) => leg.startDate <= lastDate && leg.endDate >= firstDate,
          )
        : [],
    [legs, firstDate, lastDate],
  );
}

/**
 * The all-day notes rendered by the week rail: unique across `dayViews`, and
 * only those whose whole range falls on the visible dates.
 */
function useWeekAllDayNotes(dayViews: DayView[]): AllDayTripNote[] {
  return useMemo(() => {
    const dates = new Set(dayViews.map((day) => day.date));
    const unique = new Map<string, AllDayTripNote>();
    for (const day of dayViews) {
      for (const block of day.allDayBlocks)
        unique.set(block.note.id, block.note);
    }
    return [...unique.values()]
      .filter(
        (note) =>
          dates.has(note.allDayStartDate) && dates.has(note.allDayEndDate),
      )
      .sort(
        (left, right) =>
          left.allDayStartDate.localeCompare(right.allDayStartDate) ||
          left.name.localeCompare(right.name),
      );
  }, [dayViews]);
}

/**
 * The week view's all-day row: legs and all-day notes share one grid aligned
 * to the date columns and are packed into common lanes, so a second row only
 * appears when two ranges fall on the same date.
 */
export function WeekAllDayArea({
  dayViews,
  showLegBand,
  isReadOnly,
}: {
  dayViews: DayView[];
  showLegBand: boolean;
  isReadOnly: boolean;
}) {
  const visibleLegs = useVisibleLegs(dayViews);
  const legs = useMemo(
    () => (showLegBand ? visibleLegs : []),
    [showLegBand, visibleLegs],
  );
  const notes = useWeekAllDayNotes(dayViews);
  const { legLanes, noteLanes } = useMemo(
    () =>
      packAllDayLanes(
        legs,
        notes.map((note) => ({
          id: note.id,
          startDate: note.allDayStartDate,
          endDate: note.allDayEndDate,
        })),
      ),
    [legs, notes],
  );

  return (
    <div
      className="relative grid gap-x-2.5 gap-y-1"
      style={{
        gridTemplateColumns: `repeat(${dayViews.length}, minmax(0, 1fr))`,
      }}
      data-testid="week-all-day-area"
    >
      {showLegBand && (
        <CalendarLegBand
          dayViews={dayViews}
          legs={legs}
          lanes={legLanes}
          isReadOnly={isReadOnly}
        />
      )}
      <WeekAllDayRail
        dayViews={dayViews}
        notes={notes}
        lanes={noteLanes}
        isReadOnly={isReadOnly}
      />
      {showLegBand && legs.length === 0 && notes.length === 0 && (
        <span className="col-span-full text-2xs italic text-tertiary">
          No legs cover these dates
        </span>
      )}
    </div>
  );
}
